import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
// Removed Redux auth selectors - now using useAuth() hook
import { useUserData } from '../../hooks/useUserData';
import { getProvidersByFacility } from '../../services/providerService';
import { useNavigate, Link } from 'react-router-dom';
import { updateAppointmentAsync } from '../../redux/slices/appointmentsSlice';
import { 
  Button, 
  Paper, 
  Box, 
  Typography, 
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  InputLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ViewListIcon from '@mui/icons-material/ViewList';
import TimelineIcon from '@mui/icons-material/Timeline';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/components.css';
import { useLocale } from '../../context/LocaleContext';
import { useFacilityChangeListener } from '../../hooks/useFacilityChangeListener';
import StaffTimelineView from './StaffTimelineView';
import QuickRegistrationModal from './QuickRegistrationModal';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';

import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
// Appointment type icons
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import EmergencyIcon from '@mui/icons-material/Emergency';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import LoopIcon from '@mui/icons-material/Loop';
import HotelIcon from '@mui/icons-material/Hotel';
import EventIcon from '@mui/icons-material/Event';
import ForwardIcon from '@mui/icons-material/Forward';
import VideocamIcon from '@mui/icons-material/Videocam';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ForumIcon from '@mui/icons-material/Forum';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { APPOINTMENT_STATUSES, APPOINTMENT_TYPE_ICONS, UserRole } from '../../types/enums';
import { getAppointmentsByFacility, getPatientsByFacility } from '../../services/appointmentService';
import AppointmentDetails from './AppointmentDetails';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';

// getDateRange function removed - no longer needed with API service

// Styled Components - Modern Theme
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(1.5),
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}));



const StyledDatePicker = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  '& .react-datepicker-wrapper': {
    width: '140px',
  },
  '& input': {
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid #bdbdbd',
    fontSize: '0.75rem',
    width: '100%',
    backgroundColor: 'white',
    color: '#222',
    '&::placeholder': {
      color: '#999',
    },
    '&:focus': {
      outline: 'none',
      borderColor: '#1976d2',
    },
  },
}));

const CompactChip = styled(Chip)(({ theme, status }) => ({
  fontSize: '0.8rem',
  height: '24px',
  padding: '0 8px',
  backgroundColor: status === 'scheduled' ? '#4caf50' :
                   status === 'completed' ? '#2196f3' :
                   status === 'confirmed' ? '#ff9800' :
                   '#f44336',
  color: 'white',
  fontWeight: 500,
  '& .MuiChip-label': {
    padding: '0 6px',
  },
}));

// Inline Edit Component
const InlineEditForm = ({ appointment, onSave, onCancel }) => {
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoadingProviders(true);
        const providers = await getProvidersByFacility(appointment.facilityId, 'doctor');
        setProviders(providers);
      } catch (error) {
        console.error('Error fetching providers:', error);
      } finally {
        setLoadingProviders(false);
      }
    };

    if (appointment.facilityId) {
      fetchProviders();
    }
  }, [appointment.facilityId]);

  const [formData, setFormData] = useState({
    date: appointment.date ? new Date(appointment.date) : new Date(),
    doctorName: appointment.doctorName || '',
    purpose: appointment.purpose || '',
    status: appointment.status || 'scheduled',
    notes: appointment.notes || ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      date: formData.date.toISOString()
    });
  };

  return (
    <Box sx={{ 
      p: 2, 
      backgroundColor: '#f8f9fa', 
      borderTop: '2px solid #1976d2',
      borderRadius: '0 0 8px 8px'
    }}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={2}>
            <TextField
              type="datetime-local"
              value={formData.date.toISOString().slice(0, 16)}
              onChange={(e) => handleInputChange('date', new Date(e.target.value))}
              fullWidth
              size="small"
              label="Date & Time"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Provider</InputLabel>
              <Select
                value={formData.providerId || ''}
                onChange={(e) => {
                  const provider = providers.find(p => p.id === e.target.value);
                  handleInputChange('providerId', e.target.value);
                  handleInputChange('providerName', provider?.name || '');
                }}
                label="Provider"
                disabled={loadingProviders}
              >
                <MenuItem value="">
                  <em>{loadingProviders ? 'Loading providers...' : 'Select a provider'}</em>
                </MenuItem>
                {!loadingProviders && providers.map(provider => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              value={formData.purpose}
              onChange={(e) => handleInputChange('purpose', e.target.value)}
              fullWidth
              size="small"
              label="Purpose"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                label="Status"
              >
                {['scheduled', 'confirmed', 'checked-in', 'completed', 'cancelled'].map(status => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              fullWidth
              size="small"
              label="Notes"
              multiline
              rows={1}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                type="submit"
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                sx={{ flex: 1 }}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={onCancel}
                startIcon={<CancelIcon />}
                sx={{ flex: 1 }}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

const AppointmentList = () => {
  const { t, currentLocale } = useLocale();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userRoles, userFacilities, user, defaultFacility, hasAnyRole, isAdmin } = useUserData();

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateFilter, setDateFilter] = useState('today-remaining'); // Default to 'Remaining' on landing
  const [statusFilter, setStatusFilter] = useState('all');

  const [viewMode, setViewMode] = useState('list'); // Set list as default
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [quickRegOpen, setQuickRegOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState([]);

  // Helper function to convert dateFilter to date parameters
  const getDateRangeFromFilter = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case 'today':
        return {
          startDate: today.toISOString(),
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1).toISOString()
        };
      case 'today-remaining':
        return {
          startDate: now.toISOString(),
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1).toISOString()
        };
      case 'next7':
        const next7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
        return {
          startDate: today.toISOString(),
          endDate: next7Days.toISOString()
        };
      case 'all':
        return null;
      default:
        return null;
    }
  };

  // Listen for facility changes and refresh appointments data
  const handleFacilityChange = (newFacilityId, previousFacilityId) => {
    console.log('AppointmentList: Facility changed, refreshing appointments data');
    // The component will automatically re-render when user.defaultFacility changes
  };

  useFacilityChangeListener(handleFacilityChange);

  // Handle applying custom date range
  const handleApplyCustomDates = useCallback(() => {
    if (customStartDate && customEndDate) {
      setAppliedStartDate(customStartDate);
      setAppliedEndDate(customEndDate);
    }
  }, [customStartDate, customEndDate]);

  // Custom dates are only applied when user clicks Apply button
  // No auto-apply - wait for explicit user action

  // Separate useEffect for custom date range data fetching
  useEffect(() => {
    if (dateFilter === 'custom' && appliedStartDate && appliedEndDate && user?.uid && userRoles?.length > 0) {
      const fetchCustomDateAppointments = async () => {
        setLoading(true);
        try {
          const filters = {
            startDate: appliedStartDate.toISOString(),
            endDate: appliedEndDate.toISOString(),
            statusFilter
          };
          
          // defaultFacility is now the complete facility object, extract the ID
          const customDateFacilityId = defaultFacility?.id;
          const appointmentData = await getAppointmentsByFacility(customDateFacilityId, filters);
          const filteredAppointments = statusFilter === 'all' 
            ? appointmentData 
            : appointmentData.filter(appointment => appointment.status === statusFilter);

          // Apply the same processing as in the main useEffect
          const grouped = {};
          for (const appt of filteredAppointments) {
            const key = `${appt.patientId || ''}__${appt.facilityId || ''}`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(appt);
          }
          Object.values(grouped).forEach(group => group.sort((a, b) => new Date(a.date) - new Date(b.date)));

          const withFlags = filteredAppointments.map(appt => {
            const key = `${appt.patientId || ''}__${appt.facilityId || ''}`;
            const group = grouped[key];
            const isFirstInFacility = group && group.length > 0 && group[0].id === appt.id;
            const intakeFormsDue = false;
            return { ...appt, isFirstInFacility, intakeFormsDue };
          });

          setAppointments(withFlags);
        } catch (error) {
          console.error('Error fetching custom date appointments:', error);
          setAppointments([]);
        } finally {
          setLoading(false);
        }
      };

      fetchCustomDateAppointments();
    }
  }, [appliedStartDate, appliedEndDate, dateFilter, statusFilter, user?.defaultFacility, user?.uid, userRoles, defaultFacility?.id]);

  // Fetch patients for search functionality
  useEffect(() => {
    const fetchPatients = async () => {
      if (!user?.defaultFacility) return;
      
      try {
        // defaultFacility is now the complete facility object, extract the ID
        const patientFacilityId = defaultFacility?.id;
        const patientData = await getPatientsByFacility(patientFacilityId);
        setPatients(patientData);
      } catch (error) {
        console.error('Error fetching patients for search:', error);
      }
    };

    fetchPatients();
  }, [user?.defaultFacility, defaultFacility?.id]);

  // Helper function to render appointment type icon
  const renderAppointmentTypeIcon = (appointmentType) => {
    const iconMap = {
      'DirectionsWalk': DirectionsWalkIcon,
      'Emergency': EmergencyIcon,
      'PriorityHigh': PriorityHighIcon,
      'Loop': LoopIcon,
      'Hotel': HotelIcon,
      'Event': EventIcon,
      'Forward': ForwardIcon,
      'Videocam': VideocamIcon,
      'SwapHoriz': SwapHorizIcon,
      'Visibility': VisibilityIcon,
      'Vaccines': VaccinesIcon,
      'PersonAdd': PersonAddIcon,
      'MedicalServices': MedicalServicesIcon,
      'Forum': ForumIcon,
      'Psychology': PsychologyIcon,
    };

    const iconName = APPOINTMENT_TYPE_ICONS[appointmentType];
    const IconComponent = iconMap[iconName] || EventNoteIcon; // Fallback to EventNoteIcon

    return <IconComponent sx={{ fontSize: 16, color: '#1976d2' }} />;
  };

  useEffect(() => {
    // Skip fetching if using custom date range (handled by separate useEffect)
    if (dateFilter === 'custom') {
      return;
    }
    
    // Only fetch if we have user and role information
    if (user?.uid && userRoles?.length > 0) {
      const fetchAppointments = async () => {
        setLoading(true);
        
        // Check if user has necessary permissions
        if (!user?.uid) {
          console.error('User not authenticated');
          setAppointments([]);
          setLoading(false);
          return;
        }

        // Check if user has permission to view appointments
        const allowedRoles = [UserRole.ADMIN, UserRole.FACILITY_ADMIN, UserRole.DOCTOR, UserRole.NURSE, 'receptionist'];
        if (!hasAnyRole(allowedRoles)) {
          console.error('User role does not have permission to view appointments:', userRoles.join(', '));
          setAppointments([]);
          setLoading(false);
          return;
        }

        try {
          // defaultFacility is now the complete facility object, extract the ID
          const facilityId = user?.defaultFacility?.id;
          if (!facilityId) {
            console.warn('No default facility set for user');
            setAppointments([]);
            setLoading(false);
            return;
          }

          // // Always filter by user's default facility FIRST (for security rules)
          // // constraints.push(where('facilityId', '==', defaultFacilityId));

          // // Add user context for security rules - some rules might require this
          // if (role === UserRole.FACILITY_ADMIN) {
          //   // For facility admins, might need additional constraints
          //   // This depends on your Firestore security rules
          // }

          // // Custom date range filter logic
          // if (dateFilter === 'custom') {
          //   if (customStartDate && customEndDate) {
          //     const start = new Date(customStartDate);
          //     start.setHours(0, 0, 0, 0);
          //     const end = new Date(customEndDate);
          //     end.setHours(23, 59, 59, 999);
          //     constraints.push(where('date', '>=', start));
          //     constraints.push(where('date', '<=', end));
          //   } else {
          //     setAppointments([]);
          //     setLoading(false);
          //     return;
          //   }
          // } else if (selectedDate) {
          //   const startOfDay = new Date(selectedDate);
          //   startOfDay.setHours(0, 0, 0, 0);
          //   const endOfDay = new Date(selectedDate);
          //   endOfDay.setHours(23, 59, 59, 999);
          //   constraints.push(where('date', '>=', startOfDay));
          //   constraints.push(where('date', '<=', endOfDay));
          // } else if (dateFilter !== 'all') {
          //   const dateRange = getDateRange(dateFilter);
          //   if (dateRange) {
          //     constraints.push(where('date', '>=', dateRange.start));
          //     constraints.push(where('date', '<=', dateRange.end));
          //   }
          // }

          // // Apply all constraints to query
          // const finalQuery = query(appointmentsQuery, ...constraints);

          // console.log('Fetching appointments with constraints:', {
          //   facilityId: defaultFacilityId,
          //   userRole: role,
          //   userId: user.uid,
          //   dateFilter
          // });

          // const querySnapshot = await getDocs(finalQuery);
          // const appointmentData = querySnapshot.docs.map(doc => {
          //   const data = doc.data();
          //   // Convert all Firestore Timestamps to serializable values
          //   const serializedData = { ...data };
          //   Object.keys(serializedData).forEach(key => {
          //     const value = serializedData[key];
          //     if (value && typeof value.toDate === 'function') {
          //       serializedData[key] = value.toDate().toISOString();
          //     }
          //   });
            
          //   return {
          //     id: doc.id,
          //     ...serializedData,
          //     // Ensure date is stored as ISO string, not Date object
          //     date: serializedData.date || new Date().toISOString()
          //   };
          // });

          // console.log(`Successfully fetched ${appointmentData.length} appointments`);

          // // Apply status filter
          // const filteredAppointments = statusFilter === 'all' 
          //   ? appointmentData 
          //   : appointmentData.filter(appointment => appointment.status === statusFilter);

          // // Compute isFirstInFacility and intakeFormsDue for each appointment
          // // Group by patientId + facilityId
          // const grouped = {};
          // for (const appt of filteredAppointments) {
          //   const key = `${appt.patientId || ''}__${appt.facilityId || ''}`;
          //   if (!grouped[key]) grouped[key] = [];
          //   grouped[key].push(appt);
          // }
          // // For each group, sort by date ascending
          // Object.values(grouped).forEach(group => group.sort((a, b) => new Date(a.date) - new Date(b.date)));

          // // Mark isFirstInFacility
          // const withFlags = filteredAppointments.map(appt => {
          //   const key = `${appt.patientId || ''}__${appt.facilityId || ''}`;
          //   const group = grouped[key];
          //   const isFirstInFacility = group && group.length > 0 && group[0].id === appt.id;
          //   // Placeholder for intakeFormsDue: always false for now
          //   // In the future, set to true if no intake survey completed in last 12 months
          //   const intakeFormsDue = false;
          //   return { ...appt, isFirstInFacility, intakeFormsDue };
          // });

          // setAppointments(withFlags);

          // Convert dateFilter to proper date parameters
          const dateRange = getDateRangeFromFilter(dateFilter);
          const mainFacilityId = defaultFacility?.id;
          
          // Build filters object with date parameters
          const filters = {};
          if (dateRange) {
            filters.startDate = dateRange.startDate;
            filters.endDate = dateRange.endDate;
          }
          if (statusFilter !== 'all') {
            filters.statusFilter = statusFilter;
          }
          
          const appointmentData = await getAppointmentsByFacility(mainFacilityId, filters);
          const filteredAppointments = statusFilter === 'all' 
            ? appointmentData 
            : appointmentData.filter(appointment => appointment.status === statusFilter);

          // Compute isFirstInFacility and intakeFormsDue for each appointment
          // Group by patientId + facilityId
          const grouped = {};
          for (const appt of filteredAppointments) {
            const key = `${appt.patientId || ''}__${appt.facilityId || ''}`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(appt);
          }
          // For each group, sort by date ascending
          Object.values(grouped).forEach(group => group.sort((a, b) => new Date(a.date) - new Date(b.date)));

          // Mark isFirstInFacility
          const withFlags = filteredAppointments.map(appt => {
            const key = `${appt.patientId || ''}__${appt.facilityId || ''}`;
            const group = grouped[key];
            const isFirstInFacility = group && group.length > 0 && group[0].id === appt.id;
            // Placeholder for intakeFormsDue: always false for now
            // In the future, set to true if no intake survey completed in last 12 months
            const intakeFormsDue = false;
            return { ...appt, isFirstInFacility, intakeFormsDue };
          });

          setAppointments(withFlags);

        } catch (error) {
          console.error('Error fetching appointments:', error);
          
          // Provide specific error handling for permissions
          if (error.code === 'permission-denied') {
            console.error('Permission denied: User does not have access to appointments collection');
            console.error('User roles:', userRoles.join(', '), 'User ID:', user?.uid, 'Facility:', user?.defaultFacility);
            
            // Set a user-friendly message
            setSuccessMessage(`Permission denied: Your role (${userRoles.join(', ')}) does not have access to view appointments. Please contact your administrator.`);
            setTimeout(() => setSuccessMessage(''), 5000);
          }
          
          setAppointments([]);
        } finally {
          setLoading(false);
        }
      };

      fetchAppointments();
    } else {
      setLoading(false);
    }
  }, [selectedDate, dateFilter, statusFilter, user?.defaultFacility, user?.uid, userRoles, hasAnyRole, currentLocale, defaultFacility?.id]);

  // Filter appointments based on search term
  const filteredAppointments = useMemo(() => {
    if (!searchTerm.trim()) {
      return appointments;
    }

    const searchLower = searchTerm.toLowerCase();
    
    return appointments.filter(appointment => {
      // Search by patient name (from appointment data)
      if (appointment.patientName?.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search by patient data (MRN, DOB, etc.)
      const patient = patients.find(p => p.id === appointment.patientId);
      if (patient) {
        // Search by MRN
        if (patient.mrn?.toLowerCase().includes(searchLower)) {
          return true;
        }
        
        // Search by date of birth
        if (patient.dateOfBirth) {
          const dob = new Date(patient.dateOfBirth);
          const dobString = dob.toLocaleDateString();
          if (dobString.includes(searchLower)) {
            return true;
          }
        }
        
        // Search by first name and last name separately
        if (patient.firstName?.toLowerCase().includes(searchLower) ||
            patient.lastName?.toLowerCase().includes(searchLower)) {
          return true;
        }
      }

      return false;
    });
  }, [appointments, patients, searchTerm]);

  // Reset pagination when search term changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);



  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setEditMode(false);
    setDetailsOpen(true);
  };

  // const handleEditDetails = (appointment) => {
  //   setSelectedAppointment(appointment);
  //   setEditMode(true);
  //   setDetailsOpen(true);
  // };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setEditMode(false);
    setSelectedAppointment(null);
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    setSelectedDate(null); // Clear custom date when using quick filters
  };

  // Inline edit handlers
  const handleRowClick = (row) => {
    setEditingRow(editingRow === row.id ? null : row.id);
  };

  const handleInlineSave = async (updatedData) => {
    try {
      // Check if status is being changed to checked-in and provider is missing
      if (updatedData.status === 'checked-in' && !updatedData.providerId) {
        const confirmCheckin = window.confirm('No provider has been selected. Do you want to continue with check-in?');
        if (!confirmCheckin) {
          return;
        }
      }

      // Show loading state
      setAppointments(prev => prev.map(appt =>
        appt.id === editingRow ? { ...appt, ...updatedData, status: 'updating...' } : appt
      ));
      
      // Update appointment using the service
      await dispatch(updateAppointmentAsync({ 
        appointmentId: editingRow, 
        updateData: updatedData 
      })).unwrap();
      
      // Update local state with the new data
      setAppointments(prev => prev.map(appt =>
        appt.id === editingRow ? { ...appt, ...updatedData } : appt
      ));
      
      setSuccessMessage('Appointment updated successfully!');
      setEditingRow(null);
    } catch (error) {
      console.error('Error updating appointment:', error);
      setSuccessMessage('Failed to update appointment. Please try again.');
    }
  };

  const handleInlineCancel = () => {
    setEditingRow(null);
  };

  // Status options for inline edit
  const statusOptions = APPOINTMENT_STATUSES || ['scheduled', 'confirmed', 'checked-in', 'completed', 'cancelled'];

  // Compact columns removed - not used in current implementation

  // Full columns for list view - removed as not used

  // Slice appointments for pagination
  const paginatedAppointments = filteredAppointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <div className="appointments-page">
      <Box sx={{ maxWidth: timelineExpanded ? '100vw' : '1600px', margin: '0 auto', p: 2 }}>
        {/* Ribbon/Header: Title and View Toggle */}
        <HeaderCard>
          <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              {/* Title and View Toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', flexShrink: 0 }}>
                  {t('appointments.title')}
                </Typography>
                <Tabs
                  value={viewMode}
                  onChange={(e, newValue) => setViewMode(newValue)}
                  sx={{
                    minHeight: 'auto',
                    '& .MuiTab-root': {
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.875rem',
                      minHeight: 'auto',
                      py: 0.5,
                      px: 1.5,
                      '&.Mui-selected': {
                        color: 'white',
                      },
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: 'white',
                    },
                  }}
                >
                  <Tab icon={<ViewListIcon />} label="List" value="list" />
                  <Tab icon={<TimelineIcon />} label="Timeline" value="timeline" />
                  <Tab icon={<VerticalSplitIcon />} label="Split View" value="split" />
                </Tabs>
              </Box>
              {/* Action Buttons */}
              {viewMode === 'timeline' && (
                <IconButton
                  onClick={() => setTimelineExpanded(!timelineExpanded)}
                  sx={{ color: 'white' }}
                  size="small"
                >
                  {timelineExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              )}
              {hasAnyRole([UserRole.ADMIN, UserRole.FACILITY_ADMIN, UserRole.DOCTOR, UserRole.NURSE]) && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                    onClick={() => navigate('/appointments/new')}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      px: 2,
                      py: 0.75,
                      flexShrink: 0,
                    }}
                  >
                    {t('appointments.newAppointment')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setQuickRegOpen(true)}
                    size="small"
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      px: 2,
                      py: 0.75,
                      ml: 1,
                      borderColor: 'white',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        borderColor: 'white',
                      },
                    }}
                  >
                    Quick Registration + Appointment
                  </Button>
                </>
              )}
            </Box>
          </CardContent>
        </HeaderCard>
        {/* Pills/Filters Row: Just above the table/list */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, justifyContent: 'flex-start', mb: 2, mt: 1 }}>
          {/* Quick Date Filters */}
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Button
              size="small"
              variant={dateFilter === 'today' ? 'contained' : 'outlined'}
              onClick={() => handleDateFilterChange('today')}
              sx={{
                borderRadius: '999px',
                fontSize: '0.8rem',
                fontWeight: 500,
                textTransform: 'none',
                px: 1.5,
                py: 0.25,
                minWidth: 'auto',
                height: 28,
                ...(dateFilter === 'today' ? {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': { backgroundColor: '#115293' },
                } : {
                  backgroundColor: 'white',
                  color: '#222',
                  border: '1px solid #bdbdbd',
                  '&:hover': { backgroundColor: '#e3f0fd', borderColor: '#1976d2', color: '#1976d2' },
                }),
              }}
            >
              Today
            </Button>
            <Button
              size="small"
              variant={dateFilter === 'today-remaining' ? 'contained' : 'outlined'}
              onClick={() => handleDateFilterChange('today-remaining')}
              sx={{
                borderRadius: '999px',
                fontSize: '0.8rem',
                fontWeight: 500,
                textTransform: 'none',
                px: 1.5,
                py: 0.25,
                minWidth: 'auto',
                height: 28,
                ...(dateFilter === 'today-remaining' ? {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': { backgroundColor: '#115293' },
                } : {
                  backgroundColor: 'white',
                  color: '#222',
                  border: '1px solid #bdbdbd',
                  '&:hover': { backgroundColor: '#e3f0fd', borderColor: '#1976d2', color: '#1976d2' },
                }),
              }}
            >
              Remaining Today
            </Button>
            <Button
              size="small"
              variant={dateFilter === 'next7' ? 'contained' : 'outlined'}
              onClick={() => handleDateFilterChange('next7')}
              sx={{
                borderRadius: '999px',
                fontSize: '0.8rem',
                fontWeight: 500,
                textTransform: 'none',
                px: 1.5,
                py: 0.25,
                minWidth: 'auto',
                height: 28,
                ...(dateFilter === 'next7' ? {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': { backgroundColor: '#115293' },
                } : {
                  backgroundColor: 'white',
                  color: '#222',
                  border: '1px solid #bdbdbd',
                  '&:hover': { backgroundColor: '#e3f0fd', borderColor: '#1976d2', color: '#1976d2' },
                }),
              }}
            >
              7 Days
            </Button>
            <Button
              size="small"
              variant={dateFilter === 'custom' ? 'contained' : 'outlined'}
              onClick={() => {
                setDateFilter('custom');
                setCustomStartDate(null);
                setCustomEndDate(null);
                setAppliedStartDate(null);
                setAppliedEndDate(null);
              }}
              sx={{
                borderRadius: '999px',
                fontSize: '0.8rem',
                fontWeight: 500,
                textTransform: 'none',
                px: 1.5,
                py: 0.25,
                minWidth: 'auto',
                height: 28,
                ...(dateFilter === 'custom' ? {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': { backgroundColor: '#115293' },
                } : {
                  backgroundColor: 'white',
                  color: '#222',
                  border: '1px solid #bdbdbd',
                  '&:hover': { backgroundColor: '#e3f0fd', borderColor: '#1976d2', color: '#1976d2' },
                }),
              }}
            >
              Custom
            </Button>
          </Box>
          {/* Show date picker when custom is selected */}
          {dateFilter === 'custom' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                From:
              </Typography>
              <StyledDatePicker>
                <DatePicker
                  selected={customStartDate}
                  onChange={date => setCustomStartDate(date)}
                  selectsStart
                  startDate={customStartDate}
                  endDate={customEndDate}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Start date"
                  isClearable
                />
              </StyledDatePicker>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                To:
              </Typography>
              <StyledDatePicker>
                <DatePicker
                  selected={customEndDate}
                  onChange={date => setCustomEndDate(date)}
                  selectsEnd
                  startDate={customStartDate}
                  endDate={customEndDate}
                  minDate={customStartDate}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="End date"
                  isClearable
                />
              </StyledDatePicker>
              {/* Apply button for custom dates */}
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDates}
                disabled={!customStartDate || !customEndDate}
                sx={{
                  ml: 1,
                  minWidth: 'auto',
                  px: 2,
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderRadius: '999px',
                  textTransform: 'none'
                }}
              >
                Apply
              </Button>
            </Box>
          )}

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 80, ml: 1 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: '999px',
                backgroundColor: 'white',
                color: '#222',
                border: '1px solid #bdbdbd',
                fontSize: '0.85rem',
                fontWeight: 500,
                height: '32px',
                px: 2,
                py: 0.5,
                '& .MuiSelect-select': {
                  borderRadius: '999px',
                  py: 0.5,
                  px: 2,
                  color: '#222',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSvgIcon-root': {
                  color: '#1976d2',
                },
              }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>All</MenuItem>
              {statusOptions.map(opt => (
                <MenuItem key={opt} value={opt} sx={{ fontSize: '0.85rem' }}>
                  {opt.charAt(0).toUpperCase() + opt.slice(1).replace('-', ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search by patient name, MRN, or DOB..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              minWidth: 280,
              ml: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '999px',
                backgroundColor: 'white',
                border: '1px solid #bdbdbd',
                fontSize: '0.85rem',
                fontWeight: 500,
                height: '32px',
                '&:hover': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused': {
                  borderColor: '#1976d2',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiInputBase-input': {
                  py: 0.5,
                  px: 2,
                  color: '#222',
                  '&::placeholder': {
                    color: '#999',
                    opacity: 1,
                  },
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                      {filteredAppointments.length} results
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      sx={{ 
                        color: '#999',
                        '&:hover': { color: '#666' },
                        p: 0.5
                      }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Main Content Area */}
        {viewMode === 'list' ? (
          // List View Only (MUI Table)
          <ModernCard>
            <CardContent>
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell>Doctor</TableCell>
                      <TableCell>Purpose</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Facility</TableCell>
                      <TableCell>Encounter</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedAppointments.map(row => (
                      <React.Fragment key={row.id}>
                        <TableRow
                          onClick={() => handleRowClick(row)}
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            backgroundColor: editingRow === row.id ? '#e3f2fd' : 'inherit',
                            '&:hover': {
                              backgroundColor: editingRow === row.id ? '#e3f2fd' : '#f5f5f5'
                            }
                          }}
                        >
                        <TableCell>
                          <Box sx={{ fontSize: '0.9rem' }}>
                            {new Date(row.date).toLocaleDateString()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ fontSize: '0.9rem', color: '#666' }}>
                            {new Date(row.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {/* Patient name with icons */}
                          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', maxWidth: '100%' }}>
                            <Box sx={{ position: 'absolute', top: 0, left: 0 }}>
                              <Tooltip title={`Type: ${row.appointmentType || ''}`} placement="top-start">
                                {renderAppointmentTypeIcon(row.appointmentType)}
                              </Tooltip>
                            </Box>
                            <Box sx={{ fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', pl: 2.5, pr: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
                              {row.patientName}
                              {editingRow === row.id && (
                                <EditIcon sx={{ fontSize: 16, color: '#1976d2', ml: 0.5 }} />
                              )}
                              {row.isFirstInFacility && (
                                <Tooltip title="New Patient (First Appointment in Facility)" placement="right">
                                  <FiberNewIcon sx={{ fontSize: 18, color: '#43a047', ml: 0.5, verticalAlign: 'middle' }} />
                                </Tooltip>
                              )}
                              {row.intakeFormsDue && (
                                <Tooltip title="Intake Forms Due" placement="right">
                                  <AssignmentLateIcon sx={{ fontSize: 18, color: '#fbc02d', ml: 0.5, verticalAlign: 'middle' }} />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={row.doctorName}>
                            <Box sx={{ fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                              {row.doctorName}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={row.purpose}>
                            <Box sx={{ fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                              {row.purpose}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <CompactChip label={t(`appointments.${row.status}`)} status={row.status} size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                            {row.facilityName}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {row.encounterId ? (
                            <Link
                              to={`/encounters/${row.encounterId}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'none',
                                color: '#1976d2',
                                fontSize: '0.85rem',
                                fontWeight: 500,
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.textDecoration = 'underline';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.textDecoration = 'none';
                              }}
                            >
                              <MedicalServicesIcon sx={{ fontSize: 18, mr: 0.5 }} />
                              View Encounter
                            </Link>
                          ) : (
                            <Box sx={{ fontSize: '0.85rem', color: '#999' }}>
                              —
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                      {editingRow === row.id && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ p: 0 }}>
                            <InlineEditForm
                              appointment={row}
                              onSave={handleInlineSave}
                              onCancel={handleInlineCancel}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filteredAppointments.length}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={e => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              </TableContainer>
            </CardContent>
          </ModernCard>
        ) : viewMode === 'timeline' ? (
          // Timeline View Only
          <ModernCard sx={{ height: '80vh' }}>
            <StaffTimelineView
              key={`timeline-${defaultFacility?.id}`}
              selectedFacility={defaultFacility?.id}
              onAppointmentClick={handleViewDetails}
            />
          </ModernCard>
        ) : (
          // Split View (Default) - Optimized for compact display
          <Grid container spacing={2} sx={{ height: '80vh' }}>
            <Grid item xs={12} md={6}>
              <ModernCard sx={{ height: '100%' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1.5 }}>
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Time</TableCell>
                            <TableCell>Patient</TableCell>
                            <TableCell>Doctor</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedAppointments.map(row => (
                            <React.Fragment key={row.id}>
                              <TableRow
                                onClick={() => handleRowClick(row)}
                                hover
                                sx={{ 
                                  cursor: 'pointer',
                                  backgroundColor: editingRow === row.id ? '#e3f2fd' : 'inherit',
                                  '&:hover': {
                                    backgroundColor: editingRow === row.id ? '#e3f2fd' : '#f5f5f5'
                                  }
                                }}
                              >
                              <TableCell>
                                <Box sx={{ fontSize: '0.9rem' }}>
                                  {new Date(row.date).toLocaleDateString()}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ fontSize: '0.9rem', color: '#666' }}>
                                  {new Date(row.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {/* Patient name with icons */}
                                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', maxWidth: '100%' }}>
                                  <Box sx={{ position: 'absolute', top: 0, left: 0 }}>
                                    <Tooltip title={`Type: ${row.appointmentType || ''}`} placement="top-start">
                                      {renderAppointmentTypeIcon(row.appointmentType)}
                                    </Tooltip>
                                  </Box>
                                  <Box sx={{ fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', pl: 2.5, pr: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
                                    {row.patientName}
                                    {editingRow === row.id && (
                                      <EditIcon sx={{ fontSize: 16, color: '#1976d2', ml: 0.5 }} />
                                    )}
                                    {row.isFirstInFacility && (
                                      <Tooltip title="New Patient (First Appointment in Facility)" placement="right">
                                        <FiberNewIcon sx={{ fontSize: 18, color: '#43a047', ml: 0.5, verticalAlign: 'middle' }} />
                                      </Tooltip>
                                    )}
                                    {row.intakeFormsDue && (
                                      <Tooltip title="Intake Forms Due" placement="right">
                                        <AssignmentLateIcon sx={{ fontSize: 18, color: '#fbc02d', ml: 0.5, verticalAlign: 'middle' }} />
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Tooltip title={row.doctorName}>
                                  <Box sx={{ fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                                    {row.doctorName}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <CompactChip label={t(`appointments.${row.status}`)} status={row.status} size="small" />
                              </TableCell>
                            </TableRow>
                            {editingRow === row.id && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ p: 0 }}>
                                  <InlineEditForm
                                    appointment={row}
                                    onSave={handleInlineSave}
                                    onCancel={handleInlineCancel}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </CardContent>
              </ModernCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <ModernCard sx={{ height: '100%' }}>
                <StaffTimelineView
                  key={`timeline-split-${defaultFacility?.id}`}
                  selectedFacility={defaultFacility?.id}
                  onAppointmentClick={handleViewDetails}
                />
              </ModernCard>
            </Grid>
          </Grid>
        )}
      </Box>

      <QuickRegistrationModal
        open={quickRegOpen}
        onClose={() => setQuickRegOpen(false)}
        onSuccess={() => setQuickRegOpen(false)}
        facilities={userFacilities}
      />
      {selectedAppointment && (
        <AppointmentDetails
          appointment={selectedAppointment}
          open={detailsOpen}
          onClose={handleCloseDetails}
          editMode={editMode}
          onCreateEncounter={(appt) => {
            handleCloseDetails();
          }}
          onUpdate={async (appt) => {
            try {
              // Only pass the edited fields, not the entire appointment object
              const { id, ...updateData } = appt;
              await dispatch(updateAppointmentAsync({ 
                appointmentId: id, 
                updateData 
              })).unwrap();
              setSuccessMessage('Appointment updated successfully!');
              handleCloseDetails();
            } catch (error) {
              console.error('Failed to update appointment:', error);
              // The error will be handled by the AppointmentDetails component
              throw error;
            }
          }}
        />
      )}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AppointmentList;

